import React from 'react';
import { Check, X } from 'lucide-react';

/**
 * Password-strength meter + live requirements checklist.
 * Matches backend policy (utils/password_policy.py):
 *   min 8 chars, one uppercase, one lowercase, one number, one special char.
 */
export const scorePassword = (pw = '') => {
  if (!pw) return { score: 0, valid: false, checks: {} };
  const checks = {
    length: pw.length >= 8 && pw.length <= 128,
    upper:  /[A-Z]/.test(pw),
    lower:  /[a-z]/.test(pw),
    digit:  /\d/.test(pw),
    // eslint-disable-next-line no-useless-escape
    symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(pw),
  };
  const valid = Object.values(checks).every(Boolean);
  // 0..4 strength (based on the four content rules; length is required, not scored).
  let score = ['upper', 'lower', 'digit', 'symbol'].filter((k) => checks[k]).length;
  if (pw.length >= 12 && valid) score = 4;
  return { score, valid, checks };
};

/** Convenience: is this password acceptable by policy? */
export const isPasswordValid = (pw = '') => scorePassword(pw).valid;

const LEVELS = [
  { label: 'Too weak',  color: 'bg-red-500',    text: 'text-red-600' },
  { label: 'Weak',      color: 'bg-orange-500', text: 'text-orange-600' },
  { label: 'Fair',      color: 'bg-amber-500',  text: 'text-amber-600' },
  { label: 'Good',      color: 'bg-teal-500',   text: 'text-teal-700' },
  { label: 'Strong',    color: 'bg-teal-600',   text: 'text-teal-700' },
];

const HINTS = [
  { key: 'length', label: 'At least 8 characters' },
  { key: 'upper',  label: 'One uppercase letter' },
  { key: 'lower',  label: 'One lowercase letter' },
  { key: 'digit',  label: 'One number' },
  { key: 'symbol', label: 'One special character (!@#$…)' },
];

const PasswordStrengthMeter = ({ password = '' }) => {
  const { score, checks } = scorePassword(password);
  const lvl = LEVELS[score] || LEVELS[0];
  return (
    <div className="space-y-2" data-testid="password-strength-meter">
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= score ? lvl.color : 'bg-slate-200'}`}
            data-testid={`password-strength-bar-${i}`}
          />
        ))}
        <span
          className={`text-xs font-semibold ml-2 ${lvl.text}`}
          data-testid="password-strength-label"
        >
          {password ? lvl.label : ''}
        </span>
      </div>
      {password && (
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]"
          data-testid="password-requirements-list"
        >
          {HINTS.map((h) => {
            const ok = !!checks[h.key];
            return (
              <li
                key={h.key}
                data-testid={`password-req-${h.key}-${ok ? 'ok' : 'missing'}`}
                className={`flex items-center gap-1 ${ok ? 'text-teal-700' : 'text-slate-500'}`}
              >
                {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {h.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
